import React from 'react';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import {
  ICON_ALL_INCLUSIVE,
  ICON_ANY,
  ICON_BREAKFAST,
  ICON_FULL_BOARD,
  ICON_HALF_BOARD,
  ICON_ROOM_ONLY,
} from '@/app/components/search/components/Filters/verticalFilters/Fields/getMealPlanField/getMealPlanIcon.tsx';
import { getMultilistboxValue } from '../../utils';
import FilterContainer from '../../FilterContainer';
import type { CommonProps } from '../CommonProps';
import type { AccommodationMealPlan } from '../../../../../utils/api/filtersAPIResponseTypes';
import MealPlan from './MealPlan';
import { AccomodationMealPlanContainer, AccomodationMealPlanName } from './styled';
import { type ControlProps, ControlType } from '../../../../Form';

export const getMealPlanCopy = (t: TranslationFunction) => [
  t('vacation.oss.components.searchfilters.filters.meal.any'),
  t('vacation.oss.components.searchresults.filterTags.meal.1'),
  t('vacation.oss.components.searchresults.filterTags.meal.2'),
  t('vacation.oss.components.searchresults.filterTags.meal.3'),
  t('vacation.oss.components.searchresults.filterTags.meal.4'),
  t('vacation.oss.components.searchresults.filterTags.meal.5'),
];

export const BOARD_CLASS_ICON_INDEX: { [key in BoardClassType]: number } = {
  ACCOMODATION_ONLY: 1,
  ACCOMODATION_AND_BREAKFAST: 2,
  HALF_BOARD: 3,
  FULL_BOARD: 4,
  ALL_INCLUSIVE: 5,
};

export const getMealPlanIcon = () => [
  ICON_ANY,
  ICON_ROOM_ONLY,
  ICON_BREAKFAST,
  ICON_HALF_BOARD,
  ICON_FULL_BOARD,
  ICON_ALL_INCLUSIVE,
];

export { getMealPlanEvoIcon } from '@/app/components/search/components/Filters/verticalFilters/Fields/getMealPlanField/getMealPlanIcon.tsx';

const getMealPlanOption = ({
  value,
  isWaiting,
  accommodationMealPlan,
  t,
}: {
  value: number;
  isWaiting: boolean;
  accommodationMealPlan: AccommodationMealPlan | null;
  t: TranslationFunction;
}) => ({
  value,
  label: (
    <>
      <AccomodationMealPlanContainer>{getMealPlanIcon()[value]}</AccomodationMealPlanContainer>
      <AccomodationMealPlanName>
        {getMealPlanCopy(t)[value]}
        <br />
      </AccomodationMealPlanName>
      {accommodationMealPlan && (
        <div style={{ color: '#8c8c92' }}>
          {accommodationMealPlan[value !== 0 ? value : 255]
            ? `(${accommodationMealPlan[value !== 0 ? value : 255]})`
            : '(0)'}
        </div>
      )}
    </>
  ),
  disabled:
    value === 0 ? isWaiting : isWaiting || (accommodationMealPlan && !accommodationMealPlan[value]) || undefined,
});

export const getMealPlanOptions = ({
  isWaiting,
  accommodationMealPlan,
  t,
}: {
  isWaiting: boolean;
  accommodationMealPlan: AccommodationMealPlan | null;
  t: TranslationFunction;
}) => [
  getMealPlanOption({ value: 0, isWaiting, accommodationMealPlan, t }),
  getMealPlanOption({ value: 1, isWaiting, accommodationMealPlan, t }),
  getMealPlanOption({ value: 2, isWaiting, accommodationMealPlan, t }),
  getMealPlanOption({ value: 3, isWaiting, accommodationMealPlan, t }),
  getMealPlanOption({ value: 4, isWaiting, accommodationMealPlan, t }),
  getMealPlanOption({ value: 5, isWaiting, accommodationMealPlan, t }),
];

type GetMealPlanField = {
  searchParams: SearchParams;
  commonProps: CommonProps;
  t: TranslationFunction;
};

const getMealPlanContainer = ({ searchParams, commonProps, t }: GetMealPlanField): ControlProps => {
  const value = getMultilistboxValue(searchParams[URLParameters.MEAL], true);
  const { dynamicFiltersResponse, isWaiting } = commonProps;
  const accommodationMealPlan = dynamicFiltersResponse ? dynamicFiltersResponse.accommodationMealPlan : null;
  const options = getMealPlanOptions({ isWaiting, accommodationMealPlan, t });

  return {
    control: ControlType.CONTAINER,
    name: FilterContainer.MealContainer,
    top: { text: t('vacation.oss.components.searchfilters.group.meal') },
    controls: [
      {
        control: ControlType.EXTERNAL,
        name: URLParameters.MEAL,
        value,
        default: [],
        options,
        component: MealPlan,
      },
    ],
  };
};

getMealPlanContainer.displayName = 'getMealPlanContainer';

export default getMealPlanContainer;
